import {
  isServicePointDeliveryGroup,
  getAdditionalServices,
  deliveryAdditionalServicesKey,
  deliverySelectedKey,
  deliveryTimeSlotKey
} from './selector';
import batchAction from 'store/batching';
import { ThunkDispatch } from 'components/Shared/connect';
import { Dispatch } from 'redux';
import { PageState } from 'models/PageState';
import { resetSection, arrayRemove, arrayPush, clearFields, change } from 'redux-form';
import { CheckoutPageModel, isCheckoutPage } from 'models/CheckoutPageModel';
import { CheckoutDeliveryItem } from 'models/CheckoutDeliveryItem';
import { UpdateRequest } from 'models/UpdateRequest';
import { updateCheckout } from 'views/CheckoutPage/actions';
import { checkoutFormName } from 'views/CheckoutPage/selector';

export type ServiceId = string | string[];

export const changeServicePoint =
  (identifier: string, servicePoint: string) => (dispatch: ThunkDispatch) => {
    dispatch(selectDeliveryOption(identifier, servicePoint));
    const change: Pick<UpdateRequest, 'deliveries'> = {
      deliveries: {
        identifier,
        selectedServicePoint: servicePoint
      }
    };
    return dispatch(updateCheckout(change));
  };

export const toggleAdditionalService =
  (serviceId: ServiceId, identifier: string, deliveryOptionId: string) =>
  (dispatch: ThunkDispatch, getState: () => PageState) => {
    const update = (id: string) => {
      const state = getState();
      if (!isCheckoutPage(state.currentPage)) {
        return;
      }
      const additionalServices = getAdditionalServices(state, identifier);
      const additionalServiceSelected = additionalServices.includes(id);
      if (additionalServiceSelected) {
        const index = additionalServices.indexOf(id);
        dispatch(removeAdditionalService(identifier, index));
      } else {
        dispatch(
          batchAction(
            addAdditionalService(identifier, id),
            selectDeliveryOption(identifier, deliveryOptionId)
          )
        );
      }
    };

    if (Array.isArray(serviceId)) {
      serviceId.map(update);
    } else {
      update(serviceId);
    }

    const change: Pick<UpdateRequest, 'deliveries'> = {
      deliveries: {
        identifier,
        additionalServices: getAdditionalServices(getState(), identifier)
      }
    };
    return dispatch(updateCheckout(change));
  };

const removeAdditionalService = (deliveryId: string, index: number) =>
  arrayRemove('checkout', deliveryAdditionalServicesKey(deliveryId), index);

const addAdditionalService = (deliveryId: string, serviceId: string) =>
  arrayPush('checkout', deliveryAdditionalServicesKey(deliveryId), serviceId);

const changeDeliveryOption = (deliveryId: string, deliveryOptionId: string) =>
  change('checkout', deliverySelectedKey(deliveryId), deliveryOptionId);

export const selectDeliveryOption =
  (deliveryId: string, deliveryOptionId: string) => (dispatch: ThunkDispatch) => {
    dispatch(changeDeliveryOption(deliveryId, deliveryOptionId));

    const change: Pick<UpdateRequest, 'deliveries'> = {
      deliveries: {
        identifier: deliveryId,
        deliveryOptionId
      }
    };
    dispatch(updateCheckout(change));
  };

const changeTimeSlot = (deliveryId: string, timeSlotId: string) =>
  change('checkout', deliveryTimeSlotKey(deliveryId), timeSlotId);

export const clearTimeSlot = (deliveryId: string) =>
  clearFields('checkout', false, false, deliveryTimeSlotKey(deliveryId));

export const selectTimeSlot =
  (deliveryId: string, timeSlotId: string) => (dispatch: ThunkDispatch) => {
    dispatch(changeTimeSlot(deliveryId, timeSlotId));

    const change: Pick<UpdateRequest, 'deliveries'> = {
      deliveries: {
        identifier: deliveryId,
        timeSlot: timeSlotId
      }
    };
    dispatch(updateCheckout(change));
  };

export const resetDeliveryOptions =
  ({ deliveries }: CheckoutPageModel) =>
  (dispatch: Dispatch) => {
    if (!deliveries) {
      return;
    }

    const updateDeliveryOption = ({
      identifier,
      deliveryOptions: [option]
    }: CheckoutDeliveryItem) => selectDeliveryOption(identifier, option.deliveryOptionIdentifier);

    const servicePointActions = deliveries
      .filter(g => isServicePointDeliveryGroup(g.deliveryOptions))
      .map(updateDeliveryOption);

    const actions = [resetSection(checkoutFormName, 'deliveries'), ...servicePointActions];

    deliveries
      .filter(g => !isServicePointDeliveryGroup(g.deliveryOptions))
      .map(g =>
        g.deliveryOptions?.map(o => {
          if (o.selected) {
            actions.push(selectDeliveryOption(g.identifier, o.deliveryOptionIdentifier));
          }
        })
      );

    dispatch(batchAction(...actions));
  };
